import React, { useState } from "react";
import formLogo from "../assets/bag.svg";
import ResumeLogo from "../assets/folder.svg";
import { Supabase } from "../utilities/Supabase";

const Careers = ({ screenInfo }) => {
  const { isDesktopModeOnMobile, width } = screenInfo;

  const [formData, setFormData] = useState({
    name: "",
    mail_id: "",
    contact: "",
    domain: "",
  });

  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const [fileName, setFileName] = useState("");

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required.";
        break;
      case "mail_id":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Enter a valid email address.";
        break;
      case "contact":
        if (!/^[6-9]{1}[0-9]{9}$/.test(value))
          error = "Enter a valid 10-digit contact number.";
        break;
      case "domain":
        if (!value) error = "Please select a domain.";
        break;
      case "resume":
        if (!resume) error = "Please upload your resume.";
        else if (resume.size > 1 * 1024 * 1024)
          error = "File must be smaller than 1 MB.";
        break;

      default:
        break;
    }
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });
    const resumeError = validateField("resume", resume);
    if (resumeError) newErrors.resume = resumeError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const maxSizeInBytes = 1 * 1024 * 1024; // 1 MB
      if (file.size > maxSizeInBytes) {
        setErrors((prev) => ({
          ...prev,
          resume: "File size should be less than 1 MB.",
        }));
        setResume(null);
        setFileName("");
        return;
      }

      // Valid file
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.resume;
        return updated;
      });
      setFileName(file.name);
      setResume(file);
    }
  };

  const handleReset = () => {
    setFormData({ name: "", mail_id: "", contact: "", domain: "" });
    setFileName("");
    setResume(null);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage({ type: "", text: "" });

    const isValid = validateAllFields();
    if (!isValid) return;

    setUploading(true);

    try {
      // Upload to Supabase
      const { data, error } = await Supabase.storage
        .from("Resumes")
        .upload(`pdfs/${Date.now()}_${resume.name}`, resume);

      if (error) {
        setStatusMessage({ type: "error", text: "File upload failed!" });
        return;
      }
      const fileUrl = `https://czfgsipbgzwvfphrxzam.supabase.co/storage/v1/object/public/resume/${data.path};`;
      const { error: dbError } = await Supabase.from("JobApllication").insert([
        { ...formData, resume: fileUrl },
      ]);

      if (dbError) {
        setStatusMessage({ type: "error", text: "Error saving form data!" });
      } else {
        setStatusMessage({
          type: "success",
          text: "Form submitted successfully!",
        });
        setTimeout(() => {
          setStatusMessage({ type: "", text: "" });
        }, 3000);

        handleReset();
      }
    } catch (err) {
      setStatusMessage({ type: "error", text: "Something went wrong!" });
      console.log("err", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`${
        isDesktopModeOnMobile && width > 768 
          ? ""
          : "lg:min-h-screen "
      }  w-full  flex  justify-center 2xl:items-center lg:pt-[8rem]  pt-[6rem] sm:py-[8rem]    px-4 sm:px-6 md:px-6 max-sm:pb-0 pb-10 lg:pb-[4.5rem]  `}
    >
      <div className="flex flex-col gap-6 sm:gap-8 text-white w-full max-w-screen-md md:max-w-[90vw] lg:max-w-[80vw] xl:max-w-[80vw] 2xl:max-w-[70vw]">
        {/* Heading Section */}
        <div className="flex flex-col gap-4 sm:gap-6 items-center text-center">
          <h1 className="poppins-semibold text-2xl sm:text-3xl md:text-4xl">
            How to Join Our Team
          </h1>

          <p className="poppins-regular text-sm text-center sm:text-base md:text-lg md:text-left leading-[1.8rem] sm:leading-[1.8rem] md:leading-[2rem]  lg:leading-[2.2rem] tracking-wide opacity-90 px-2 sm:px-4">
            At Optimum Codes, we believe in learning, growing, and building
            together. If you're passionate about technology and eager to gain
            real-world experience, this is your chance to work on
            industry-standard projects and be part of something impactful.
          </p>
        </div>
        <div className="w-full flex justify-center">
          {/* Form */}
          <div className="max-md:w-[90vw] w-[60vw] border-[1px] border-[#FFFFFF47] rounded-[0.5rem] bg-[#FFFFFF14] drop-shadow-xl shadow-2xl">
            <div className="max-md:m-3 m-6 mb-0 flex items-center max-md:p-0 p-2">
              <div className="w-10 h-10 border-[1px] border-[#ffffff5c] rounded-[0.75rem] bg-gradient-to-b from-[#34209A] to-[#523FAE] flex justify-center items-center">
                <img src={formLogo} alt="form-logo" />
              </div>
            </div>
            <div className="mx-6 my-2 px-2">
              <p className="font-bold text-[#DFDFDF]">
                Fill in your details to join us!
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="max-md:w-auto max-md:mx-3 max-md:my-2 mx-6 my-4 max-md:h-[30rem] h-[24rem] max-md:space-y-0 space-y-4">
                <div className="w-full max-md:h-[10rem] h-[4rem] flex max-md:block">
                  <div className="w-2/5 max-md:w-full h-full max-md:h-1/2">
                    <div className="h-full flex flex-col justify-center px-2">
                      <div className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] bg-[#FFFFFF22] rounded-lg">
                        <input
                          type="text"
                          name="name"
                          id="floating_outlined_name"
                          onChange={handleChange}
                          className="block px-2.5 pb-4 pt-4 w-full h-full text-sm text-white bg-[#FFFFFF22] rounded-lg  appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                          placeholder=" "
                        />
                        <label
                          htmlFor="floating_outlined_name"
                          className="absolute text-white transform duration-300 peer-placeholder-shown:-translate-y-[30px] peer-placeholder-shown:translate-x-3 peer-placeholder-shown:text-sm max-md:peer-focus:-translate-y-[3.5rem] peer-focus:-translate-y-[3.8rem] max-md:-translate-y-[3.5rem] -translate-y-[3.8rem] peer-focus:-translate-x-0 -translate-x-0 peer-focus:text-xs text-xs peer"
                        >
                          Name
                        </label>
                      </div>
                      {/* 👇 Show validation error if exists */}
                      {errors.name && (
                        <p className="text-red-400 text-xs mt-1  w-full text-center">
                          {errors.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="w-3/5 h-full max-md:w-full max-md:h-1/2">
                    <div className="h-full flex flex-col justify-center px-2">
                      <div className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] bg-[#FFFFFF22] rounded-lg">
                        <input
                          type="email"
                          onChange={handleChange}
                          name="mail_id"
                          id="floating_outlined_email"
                          className="block px-2.5 pb-4 pt-4 w-full h-full text-sm text-white bg-[#FFFFFF22] rounded-lg  appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                          placeholder=" "
                        />
                        <label
                          htmlFor="floating_outlined_email"
                          className="absolute text-white transform duration-300 peer-placeholder-shown:-translate-y-[30px] peer-placeholder-shown:translate-x-3 peer-placeholder-shown:text-sm max-md:peer-focus:-translate-y-[3.5rem] peer-focus:-translate-y-[3.8rem] max-md:-translate-y-[3.5rem] -translate-y-[3.8rem] peer-focus:-translate-x-0 -translate-x-0 peer-focus:text-xs text-xs peer"
                        >
                          Mail ID
                        </label>
                      </div>
                      {/* 👇 Show validation error if exists */}
                      {errors.mail_id && (
                        <p className="text-red-400 text-xs mt-1  w-full text-center">
                          {errors.mail_id}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-full max-md:h-[5rem] h-[4rem] flex">
                  <div className="w-full h-full flex flex-col justify-center px-2">
                    <div className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] bg-[#FFFFFF22] rounded-lg">
                      <input
                        type="tel"
                        name="contact"
                        onChange={handleChange}
                        id="floating_outlined_number"
                        className="block px-2.5 pb-4 pt-4 w-full h-full text-sm text-white bg-[#FFFFFF22] rounded-lg  appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor="floating_outlined_number"
                        className="absolute text-white transform duration-300 peer-placeholder-shown:-translate-y-[30px] peer-placeholder-shown:translate-x-3 peer-placeholder-shown:text-sm max-md:peer-focus:-translate-y-[3.5rem] peer-focus:-translate-y-[3.8rem] max-md:-translate-y-[3.5rem] -translate-y-[3.8rem] peer-focus:-translate-x-0 -translate-x-0 peer-focus:text-xs text-xs peer"
                      >
                        Contact No
                      </label>
                    </div>
                    {errors.contact && (
                      <p className="text-red-400 w-full text-center text-xs mt-1">
                        {errors.contact}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full max-md:h-[5rem] h-[4rem]">
                  <div className="w-full h-full flex flex-col justify-center px-2">
                    <div className="w-full relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] bg-[#FFFFFF22] rounded-lg ">
                      <select
                        id="domain"
                        name="domain"
                        onChange={handleChange}
                        className="block px-2 w-full h-full text-sm text-white bg-[#FFFFFF22] rounded-lg focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      >
                        <option
                          defaultValue
                          className="bg-[#FFFFFF] text-black"
                        >
                          Choose the domain
                        </option>
                        <option
                          value="Web Development"
                          className="bg-[#FFFFFF] text-black"
                        >
                          Web Development
                        </option>
                        <option
                          value="Mobile Development"
                          className="bg-[#FFFFFF] text-black"
                        >
                          Mobile Development
                        </option>
                        <option
                          value="AI Integrations"
                          className="bg-[#FFFFFF] text-black"
                        >
                          AI Integrations
                        </option>
                        <option value="SEO" className="bg-[#FFFFFF] text-black">
                          SEO
                        </option>
                      </select>
                    </div>
                    {errors.domain && (
                      <p className="text-red-400 w-full text-center text-xs mt-1">
                        {errors.domain}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full max-md:h-[5rem] h-[4rem] flex">
                  <div className="w-full h-full flex flex-col justify-center px-2">
                    <div className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] bg-[#FFFFFF22] rounded-lg">
                      <input
                        type="file"
                        accept=".pdf"
                        id="floating_outlined_resume"
                        className={
                          fileName
                            ? "block px-2.5 pb-4 pt-2 w-full h-full text-sm bg-[#FFFFFF22] rounded-lg  appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer file:hidden text-[#FFFFFF]"
                            : "block px-2.5 pb-4 pt-4 w-full h-full text-sm bg-[#FFFFFF22] rounded-lg  appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer file:hidden text-[#FFFFFF00]"
                        }
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="floating_outlined_resume"
                        className={
                          fileName
                            ? "flex absolute text-white transform duration-300 peer-placeholder-shown:-translate-y-[30px] peer-placeholder-shown:translate-x-3 peer-placeholder-shown:text-sm max-md:peer-focus:-translate-y-[3.5rem] peer-focus:-translate-y-[3.8rem] max-md:-translate-y-[3.5rem] -translate-y-[3.8rem] peer-focus:-translate-x-0 -translate-x-0 peer-focus:text-xs text-xs peer"
                            : "flex space-x-3 absolute text-white transform duration-300 -translate-y-[30px] translate-x-3 text-sm max-md:peer-focus:-translate-y-[3.8rem] peer-focus:-translate-y-[4rem] peer-focus:-translate-x-0  peer-focus:text-xs peer"
                        }
                      >
                        <span className={fileName ? "hidden" : ""}>
                          <img src={ResumeLogo} className="w-5 h-5" />
                        </span>
                        <span className={fileName ? "hidden" : ""}>
                          Upload your Resume
                        </span>
                        <span className={fileName ? "" : "hidden"}>Resume</span>
                      </label>
                    </div>
                    {fileName && (
                      <p className="text-sm text-green-300 mt-1 w-full text-center">
                        {fileName}
                      </p>
                    )}
                    {errors.resume && (
                      <p className="text-red-400 text-xs mt-1 w-full text-center">
                        {errors.resume}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full max-md:h-[5rem] h-[4rem] flex gap-4">
                  {/* Reset Button */}
                  <div className="w-1/2 h-full">
                    <div className="h-full flex flex-col justify-center px-2">
                      <div
                        className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] 
        bg-[#FFFFFF22] hover:bg-[#FFFFFF55] rounded-lg cursor-pointer 
        transition-all duration-300 ease-in-out transform 
        hover:scale-100 active:scale-95 
        shadow-md hover:shadow-white/40 active:shadow-inner"
                      >
                        <input
                          type="reset"
                          value="Reset"
                          onClick={handleReset}
                          id="floating_outlined_name"
                          className="block px-2.5 w-full h-full text-sm text-white 
          bg-transparent rounded-lg appearance-none 
          focus:outline-none focus:ring-0 peer cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="w-1/2 h-full">
                    <div className="h-full flex flex-col justify-center px-2">
                      <div
                        className="relative max-md:h-1/2 h-2/3 border border-[#FFFFFF77] 
        bg-gradient-to-l from-[#5637B2] to-[#6340CD] 
        rounded-lg cursor-pointer 
        transition-all duration-300 ease-in-out transform 
        hover:scale-100 active:scale-95 
        hover:from-[#7357df] hover:to-[#4a2fa2] 
        shadow-lg hover:shadow-violet-400/40 active:shadow-inner"
                      >
                        <input
                          type="submit"
                          value={uploading ? "Uploading..." : "Submit"}
                          id="floating_outlined_email"
                          className="block px-2.5 w-full h-full text-sm text-white 
          bg-transparent rounded-lg appearance-none 
          focus:outline-none focus:ring-0 peer cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            {statusMessage.text && (
              <div
                className={`mb-4 max-md:w-auto max-md:mx-5 max-md:my-2 mx-8 text-sm font-medium px-4 py-2 rounded-md text-center ${
                  statusMessage.type === "success"
                    ? "bg-green-500/20 text-green-300 border border-green-500"
                    : "bg-red-500/20 text-red-300 border border-red-500"
                }`}
              >
                {statusMessage.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Careers;
